import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { AuthShell } from "@/components/auth/auth-shell";
import { SignUpForm } from "@/components/auth/signup-form";
import { isAuthed, pb } from "@/lib/pb";
import { DEFAULT_BRAND, type TenantBrand, resolveTenantBrand } from "@/lib/tenant-brand";
import { useDocumentTitle } from "@/lib/use-document-title";

export const Route = createFileRoute("/signup")({
  beforeLoad: () => {
    if (isAuthed()) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: SignUpPage,
});

function SignUpPage() {
  useDocumentTitle("Create account");
  const router = useRouter();
  const queryClient = useQueryClient();

  const [brand, setBrand] = useState<TenantBrand>(DEFAULT_BRAND);
  useEffect(() => {
    setBrand(resolveTenantBrand(window.location.host));
  }, []);

  async function handleSubmit({
    email,
    password,
    name,
  }: {
    email: string;
    password: string;
    name: string;
  }) {
    // PB requires passwordConfirm at the SDK level even though we
    // already validated it in the form — the SDK forwards it verbatim
    // to the create endpoint. The collection's createRule may reject
    // this entirely (the NEET deployment likely restricts user creation
    // to support personnel via dashboard-upload). When it does, PB
    // throws a ClientResponseError which the form surfaces as-is.
    await pb.collection("auth").create({
      email,
      password,
      passwordConfirm: password,
      name,
      emailVisibility: true,
    });
    await pb.collection("auth").authWithPassword(email, password);
    queryClient.clear();
    router.history.push("/dashboard");
  }

  return (
    <AuthShell brand={brand}>
      <SignUpForm onSubmit={handleSubmit} />
    </AuthShell>
  );
}
